import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { SearchX, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <SearchX size={11} /> Challans
      </div>
      <h1 className="font-black text-3xl tracking-tight">Challan Gaps</h1>
      <p className="text-sm text-gray-500 mt-1">Detect missing challan numbers in each series</p>
    </div>
  );
}

export function ChallanGapsPage() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]    = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/challans/series");
      setSeries(r.data?.series || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalGaps = series.reduce((s, g) => s + (g.missing?.length || 0), 0);

  return (
    <div className="px-6 py-6 max-w-3xl">
      <div className="flex items-start justify-between mb-6">
        <PageHeader />
        <button onClick={load} className="mt-6 p-2 hover:bg-gray-100 rounded-lg border border-gray-200" title="Refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Scanning challan series…</div>
      ) : series.length === 0 ? (
        <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
          <SearchX size={28} className="mx-auto mb-3 text-gray-300" />
          <div className="text-sm text-gray-500">No challan series found</div>
          <div className="text-xs text-gray-400 mt-1">Create some sales or purchases with challan numbers first</div>
        </div>
      ) : (
        <>
          {totalGaps === 0 ? (
            <div className="mb-4 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
              <CheckCircle size={18} className="text-green-600" />
              <div>
                <div className="font-semibold text-green-800 text-sm">All series are complete!</div>
                <div className="text-xs text-green-600 mt-0.5">No missing challan numbers detected across {series.length} series.</div>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle size={18} className="text-amber-600" />
              <div>
                <div className="font-semibold text-amber-800 text-sm">{totalGaps} Missing Challan{totalGaps > 1 ? "s" : ""} Detected</div>
                <div className="text-xs text-amber-600 mt-0.5">Found gaps across {series.filter(s => s.missing?.length > 0).length} series</div>
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="challan-gaps-list">
            {series.map(g => (
              <div key={g.series} className={`bg-white border rounded-xl overflow-hidden ${g.missing?.length > 0 ? "border-amber-200" : "border-gray-200"}`}>
                <button onClick={() => setExpanded(expanded === g.series ? null : g.series)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                  data-testid={`series-${g.series}`}>
                  <div>
                    <span className="font-bold text-gray-800">{g.series === "default" ? "Default (no prefix)" : `Series: ${g.prefix}`}</span>
                    <span className="ml-3 text-xs text-gray-400">
                      #{g.min} – #{g.max} · {g.count} challan{g.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {g.missing?.length > 0 ? (
                      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                        {g.missing.length} missing
                      </span>
                    ) : (
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Complete</span>
                    )}
                    <span className="text-gray-400 text-xs">{expanded === g.series ? "▲" : "▼"}</span>
                  </div>
                </button>

                {expanded === g.series && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {g.missing?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">Missing Numbers:</div>
                        <div className="flex flex-wrap gap-2">
                          {g.missing.map(n => (
                            <span key={n} className="bg-amber-50 border border-amber-200 text-amber-700 text-xs font-mono font-bold px-2.5 py-1 rounded-lg">
                              {g.prefix}{n}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Recorded Challans ({g.txns?.length}):</div>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {(g.txns || []).map(t => (
                          <div key={t.id} className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="font-mono font-semibold text-gray-800 w-20">{t.challan_no}</span>
                            <span className="text-gray-400">{t.date}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${t.type === "sale" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                              {t.type}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ChallanGapsPage;
